// ./src/hooks/useTemplateCategories.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  TemplateCategory,
  TemplateCategoryService,
  TemplateCategoryFilter,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseTemplateCategoriesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<TemplateCategoryFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseTemplateCategoriesReturn {
  // Data state
  templateCategory: TemplateCategory | null;
  templateCategories: TemplateCategory[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<TemplateCategoryFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  selectedTemplateCategory: TemplateCategory | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<TemplateCategoryFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<TemplateCategory[]>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  ensureTemplateCategories: (
    templateCategories: TemplateCategory[] | null | undefined
  ) => void;

  // Search and filter operations
  setFilters: (filters: Partial<TemplateCategoryFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedTemplateCategory: (
    templateCategory: TemplateCategory | null
  ) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  templateCategoriesByName: Record<string, TemplateCategory>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useTemplateCategories = (
  options: UseTemplateCategoriesOptions = {}
): UseTemplateCategoriesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [templateCategory, setTemplateCategory] =
    useState<TemplateCategory | null>(null);
  const [templateCategories, setTemplateCategories] = useState<
    TemplateCategory[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<TemplateCategoryFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [selectedTemplateCategory, setSelectedTemplateCategory] =
    useState<TemplateCategory | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => templateCategories.length === 0 && !loading,
    [templateCategories.length, loading]
  );

  const templateCategoriesByName = useMemo(() => {
    return templateCategories.reduce((acc, templateCategory) => {
      if (templateCategory.id && templateCategory.name) {
        acc[templateCategory.name] = templateCategory;
      }
      return acc;
    }, {} as Record<string, TemplateCategory>);
  }, [templateCategories]);

  // Helper function to update template category in list if it exists, or add it if not
  const updateOrAddTemplateCategoryInList = useCallback(
    (updatedTemplateCategory: TemplateCategory) => {
      setTemplateCategories((prev) => {
        const existingIndex = prev.findIndex(
          (c) => c.id === updatedTemplateCategory.id
        );
        if (existingIndex >= 0) {
          const newTemplateCategories = [...prev];
          newTemplateCategories[existingIndex] = updatedTemplateCategory;
          return newTemplateCategories;
        } else {
          return [updatedTemplateCategory, ...prev];
        }
      });
    },
    []
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    console.log("Resetting all the template categories");
    setTemplateCategory(null);
    setTemplateCategories([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setSelectedTemplateCategory(null);
  }, []);

  // Get single template category (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await TemplateCategoryService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const templateCategoryData = result.data[0] as TemplateCategory;
          setTemplateCategory(templateCategoryData);
          updateOrAddTemplateCategoryInList(templateCategoryData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load template category";
          setLastError(errorMessage);
          setTemplateCategory(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load template category";
        setLastError(errorMessage);
        setTemplateCategory(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddTemplateCategoryInList]
  );

  // List template categories (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<TemplateCategoryFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setTemplateCategories([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await TemplateCategoryService.list(
          filter as TemplateCategoryFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTemplateCategories = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setTemplateCategories((prev) => {
              const existingIds = new Set(prev.map((c) => c.id));
              const uniqueNewTemplateCategories = newTemplateCategories.filter(
                (c) => !existingIds.has(c.id)
              );
              return [...prev, ...uniqueNewTemplateCategories];
            });
          } else {
            setTemplateCategories(newTemplateCategories);
          }

          setHasMore(newTemplateCategories.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load template categories";
          setLastError(errorMessage);
          if (!isAppending) setTemplateCategories([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load template categories";
        setLastError(errorMessage);
        if (!isAppending) setTemplateCategories([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many template categories by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<TemplateCategory[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await TemplateCategoryService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedTemplateCategories = Array.isArray(result.data)
            ? result.data
            : [];
          loadedTemplateCategories.forEach((templateCategory) => {
            updateOrAddTemplateCategoryInList(templateCategory);
          });
          setLastError(null);
          return loadedTemplateCategories;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load template categories";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load template categories";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddTemplateCategoryInList]
  );

  // Ensure template categories exist in the list (add if not already present)
  const ensureTemplateCategories = useCallback(
    (templateCategoriesToEnsure: TemplateCategory[] | null | undefined) => {
      if (
        !templateCategoriesToEnsure ||
        templateCategoriesToEnsure.length === 0
      ) {
        return;
      }

      setTemplateCategories((prev) => {
        // Create a set of existing template category IDs for fast lookup
        const existingIds = new Set(prev.map((c) => c.id));

        // Filter out template categories that already exist
        const newTemplateCategories = templateCategoriesToEnsure.filter(
          (templateCategory) =>
            templateCategory.id && !existingIds.has(templateCategory.id)
        );

        // If no new template categories to add, return previous state
        if (newTemplateCategories.length === 0) {
          return prev;
        }

        // Add new template categories to the list
        return [...prev, ...newTemplateCategories];
      });
    },
    []
  );

  // Load more template categories
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh template categories
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  useEffect(() => {
    if (autoLoad && isAuthenticated && loadedForUserId != userId) {
      console.log("=== Autoload a list of template categories");
      setLoadedForUserId(userId);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [
    autoLoad,
    isAuthenticated,
    userId,
    loadedForUserId,
    list,
    pageSize,
    initialSort,
  ]);

  return {
    templateCategory,
    templateCategories,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    selectedTemplateCategory,
    get,
    list,
    getMany,
    loadMore,
    refresh,
    ensureTemplateCategories,
    setFilters,
    setSortBy,
    setSelectedTemplateCategory,
    reset,
    templateCategoriesByName,
    lastError,
    clearError,
  };
};
